import { useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { ESCALATION_TARGET_ORDER, ESCALATION_TARGETS } from '../../../../lib/escalation-targets';
import {
  deleteOutboundAttachment,
  uploadOutboundAttachment,
  type Card,
  type OutboundAttachment,
  type RedraftReason,
  type EscalateTo,
} from '../hooks/useHatMail';

// ── Edit Flow ───────────────────────────────────────────────

interface EditFlowProps {
  card: Card;
  onSave: (editedBody: string, attachmentIds?: string[]) => void;
  onCancel: () => void;
  saving: boolean;
  autoFocusResponse?: boolean;
}

const QUICK_PHRASES = [
  "We can most definitely help you with that!",
  "Custom builds are currently running 3-4 months.",
  "We'd love to see you in the shop!",
  "Your hat is coming along beautifully.",
  "Give us a call at (888) 367-2428.",
];

export function EditFlow({
  card,
  onSave,
  onCancel,
  saving,
  autoFocusResponse = true,
}: EditFlowProps) {
  const [body, setBody] = useState(card.draft_body);
  const [attachments, setAttachments] = useState<OutboundAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSave(body, attachments.map((attachment) => attachment.id));
  };

  const handleFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (files.length === 0) return;

    setUploading(true);
    setAttachmentError(null);
    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          throw new Error(`${file.name} is not an image`);
        }
        const uploaded = await uploadOutboundAttachment(card.id, file);
        setAttachments((current) => [...current, uploaded]);
      }
    } catch (error) {
      setAttachmentError((error as Error).message || 'Could not upload image');
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = async (attachmentId: string) => {
    setDeletingIds((current) => new Set(current).add(attachmentId));
    setAttachmentError(null);
    try {
      await deleteOutboundAttachment(attachmentId);
      setAttachments((current) => current.filter((attachment) => attachment.id !== attachmentId));
    } catch (error) {
      setAttachmentError((error as Error).message || 'Could not remove image');
    } finally {
      setDeletingIds((current) => {
        const next = new Set(current);
        next.delete(attachmentId);
        return next;
      });
    }
  };

  const handleCancel = async () => {
    for (const attachment of attachments) {
      try {
        await deleteOutboundAttachment(attachment.id);
      } catch {
        // Best effort cleanup. Server-side pending attachment cleanup can sweep leftovers.
      }
    }
    onCancel();
  };

  const insertPhrase = (phrase: string) => {
    const ta = textareaRef.current;
    if (ta) {
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const before = body.slice(0, start);
      const after = body.slice(end);
      const spaceBefore = before.length > 0 && !before.endsWith(' ') && !before.endsWith('\n') ? ' ' : '';
      const newBody = before + spaceBefore + phrase + after;
      setBody(newBody);
      // Move cursor to end of inserted phrase
      requestAnimationFrame(() => {
        const pos = start + spaceBefore.length + phrase.length;
        ta.setSelectionRange(pos, pos);
        ta.focus();
      });
    } else {
      setBody((b) => b + ' ' + phrase);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-3 space-y-3">
      {/* Original email, always visible on top */}
      <div className="rounded-lg border border-gray-200 bg-white p-3 max-h-48 overflow-y-auto">
        <div className="mb-1.5 flex items-center gap-2">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
            From {card.from_name || card.from_email}
          </p>
          <span className="text-xs text-gray-300">&middot;</span>
          <p className="text-xs text-gray-400">{card.subject}</p>
        </div>
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-600">
          {card.body_full || card.body_preview || 'No email body available'}
        </p>
      </div>

      {/* Outbound image attachments */}
      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
            Images to Customer
          </p>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || saving}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50"
          >
            {uploading ? 'Uploading...' : 'Add Image'}
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFiles}
        />
        {attachments.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {attachments.map((attachment) => (
              <span
                key={attachment.id}
                className="inline-flex max-w-full items-center gap-1 rounded-lg bg-gray-100 px-2.5 py-1 text-xs text-gray-600"
              >
                <span className="max-w-[150px] truncate">{attachment.filename}</span>
                <span className="text-gray-400">
                  {attachment.size_bytes > 1048576
                    ? `${(attachment.size_bytes / 1048576).toFixed(1)} MB`
                    : `${Math.round(attachment.size_bytes / 1024)} KB`}
                </span>
                <button
                  type="button"
                  onClick={() => removeAttachment(attachment.id)}
                  disabled={deletingIds.has(attachment.id) || saving}
                  className="ml-1 rounded px-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700 disabled:opacity-50"
                  title="Remove image"
                >
                  Remove
                </button>
              </span>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400">No customer images attached</p>
        )}
        {attachmentError && (
          <p className="mt-2 text-xs text-red-500">{attachmentError}</p>
        )}
      </div>

      {/* Editable draft, below the original */}
      <div>
        <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-ghw-gold">Your response</p>
        <textarea
          ref={(el) => { textareaRef.current = el; }}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="w-full rounded-lg border border-gray-200 p-3 text-sm leading-relaxed text-gray-800 focus:border-ghw-gold focus:outline-none focus:ring-2 focus:ring-ghw-gold/20 transition-colors"
          rows={8}
          autoFocus={autoFocusResponse}
        />
      </div>

      {/* Quick insert, inserts at cursor position */}
      <div className="flex flex-wrap gap-1.5">
        {QUICK_PHRASES.map((phrase) => (
          <button
            key={phrase}
            type="button"
            onClick={() => insertPhrase(phrase)}
            className="rounded-full bg-gray-100 px-3 py-1.5 text-xs text-gray-600 hover:bg-ghw-cream active:bg-ghw-gold/20 transition-colors"
            style={{ minHeight: '32px' }}
          >
            {phrase.length > 40 ? phrase.slice(0, 40) + '...' : phrase}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button
          type="submit"
          disabled={saving || uploading}
          className="btn-touch btn-send flex-1"
        >
          {saving ? 'Saving...' : 'Save & Send'}
        </button>
        <button type="button" onClick={handleCancel} className="btn-touch btn-outlined">
          Cancel
        </button>
      </div>
    </form>
  );
}

// ── Redraft Flow ────────────────────────────────────────────

interface RedraftFlowProps {
  onSubmit: (reason: RedraftReason, note?: string) => void;
  onCancel: () => void;
  loading: boolean;
}

const REDRAFT_OPTIONS: { value: RedraftReason; label: string }[] = [
  { value: 'wrong_category', label: 'Wrong category' },
  { value: 'wrong_tone', label: 'Wrong tone' },
  { value: 'missing_info', label: 'Missing info' },
  { value: 'other', label: 'Other' },
];

export function RedraftFlow({ onSubmit, onCancel, loading }: RedraftFlowProps) {
  const [reason, setReason] = useState<RedraftReason | null>(null);
  const [note, setNote] = useState('');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!reason) return;
    onSubmit(reason, note || undefined);
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-4 text-lg font-semibold text-gray-900">What was off?</h3>
        <div className="mb-4 grid grid-cols-2 gap-2">
          {REDRAFT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setReason(opt.value)}
              className={`rounded-xl border-2 px-4 py-3 text-sm font-medium transition-colors ${
                reason === opt.value
                  ? 'border-ghw-gold bg-ghw-gold/10 text-ghw-brown'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
              style={{ minHeight: '48px' }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {reason === 'other' && (
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Tell us more..."
            className="mb-4 w-full rounded-lg border border-gray-200 p-3 text-sm focus:border-ghw-gold focus:outline-none focus:ring-2 focus:ring-ghw-gold/20"
            rows={3}
            autoFocus
          />
        )}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={!reason || loading}
            className="btn-touch btn-send flex-1 disabled:opacity-40"
          >
            {loading ? 'Redrafting...' : 'Redraft'}
          </button>
          <button type="button" onClick={onCancel} className="btn-touch btn-outlined">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Escalate Flow ───────────────────────────────────────────

interface EscalateFlowProps {
  onSubmit: (to: EscalateTo, note?: string) => void;
  onCancel: () => void;
  loading: boolean;
}

const ESCALATION_OPTIONS: { value: EscalateTo; label: string; detail: string }[] = ESCALATION_TARGET_ORDER.map((value) => ({
  value,
  label: ESCALATION_TARGETS[value].label,
  detail: ESCALATION_TARGETS[value].recipients.join(', '),
}));

export function EscalateFlow({ onSubmit, onCancel, loading }: EscalateFlowProps) {
  const [target, setTarget] = useState<EscalateTo>('foh_manager');
  const [note, setNote] = useState('');
  const selected = ESCALATION_OPTIONS.find((option) => option.value === target) ?? ESCALATION_OPTIONS[0];

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit(target, note || undefined);
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-4 text-lg font-semibold text-gray-900">Route Internally</h3>

        <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {ESCALATION_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setTarget(option.value)}
              className={`min-h-[58px] rounded-xl border-2 px-3 py-2 text-left transition-colors ${
                target === option.value
                  ? 'border-amber-400 bg-amber-50 text-gray-900'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              <span className="block text-sm font-medium">{option.label}</span>
              <span className="block truncate text-xs text-gray-400">{option.detail}</span>
            </button>
          ))}
        </div>

        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Context for recipient (optional)"
          className="mb-4 w-full rounded-lg border border-gray-200 p-3 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/20"
          rows={2}
        />

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={loading}
            className="btn-touch flex-1 rounded-xl bg-amber-500 px-4 font-medium text-white hover:bg-amber-600 active:bg-amber-700 disabled:opacity-40 transition-colors"
          >
            {loading ? 'Sending...' : `Send to ${selected.label}`}
          </button>
          <button type="button" onClick={onCancel} className="btn-touch btn-outlined">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Clear without reply ─────────────────────────────────────

interface ClearFlowProps {
  onSubmit: (note?: string) => void;
  onCancel: () => void;
  loading: boolean;
}

export function ClearFlow({ onSubmit, onCancel, loading }: ClearFlowProps) {
  const [note, setNote] = useState('');

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit(note.trim() || undefined);
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 className="mb-2 text-lg font-semibold text-gray-900">Clear without a reply</h3>
        <p className="mb-4 text-sm text-gray-600">
          No email will be sent. Use this when the customer called, or the card does not need a response.
        </p>
        <textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Optional note, such as customer called"
          className="mb-4 w-full rounded-lg border border-gray-200 p-3 text-sm focus:border-ghw-gold focus:outline-none focus:ring-2 focus:ring-ghw-gold/20"
          rows={3}
          maxLength={500}
        />
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={loading}
            className="btn-touch btn-send flex-1 disabled:opacity-40"
          >
            {loading ? 'Clearing...' : 'Clear card'}
          </button>
          <button type="button" onClick={onCancel} className="btn-touch btn-outlined">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
